/**
 * /guides/wedding-seating-plan
 *
 * "How to Create a Wedding Seating Plan: From RSVP to Every Table" — the first
 * production guide, and the head of the wedding-seating cluster.
 *
 * The article is useful on its own: eleven steps that work for anyone building a
 * seating plan, with or without Agapaé. Only the closing section names the
 * product, and it links to /wedding-seating, which is where the capability is
 * actually described. /wedding-rsvp is linked once, in the section about
 * starting from the replies, because that is the page a reader who has not
 * collected them yet genuinely needs.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * The closing section is the only place the product is described, and every
 * sentence in it is bounded by the seating capability audit that governs
 * /wedding-seating. Specifically:
 *
 *  - The seating pool is APPROVED RECEPTION ATTENDEES, not the RSVP headcount
 *    and not the guest list. `buildReceptionPool` admits a member only when the
 *    guest is active and approved, the member's review status is approved or
 *    absent, and they hold an attending response to a reception section. The
 *    copy says "confirmed and approved" and never claims parity with the
 *    overall headcount — sections 2 and 3 exist to set that expectation.
 *  - A cancellation does NOT empty a seat. The plan is not silently rearranged;
 *    the affected seating is surfaced for review and the couple decides. Never
 *    reword this into "syncs", "updates automatically" or "frees the seat".
 *  - Tables are created by the couple, capacity is the one hard limit, and
 *    preferences are advisory. Sections 4 to 7 describe this as ordinary
 *    seating advice, which is what it is.
 *  - Exports are Excel and a print-ready A4 landscape PDF. Nothing else.
 *
 * And the four things this guide must never say, because none of them exist:
 * drag-and-drop, a visual floor plan or canvas, automatic table generation, and
 * any automatic seating algorithm or reassignment.
 *
 * Plan availability: the closing section carries a note block stating that
 * seating is Heirloom-only. Keep it. Without it a reader can finish the guide
 * believing seating ships on every plan, and the correction only arrives once
 * they reach /wedding-seating.
 */

const weddingSeatingPlanEn = {
  slug: "wedding-seating-plan",

  title: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
  metaTitle:
    "How to Create a Wedding Seating Plan: From RSVP to Every Table | Agapaé",
  metaDescription:
    "Learn how to turn RSVP responses into a wedding seating plan, organize guests into tables, handle preferences, and manage last-minute changes.",

  eyebrow: "Wedding seating",

  hero: {
    src: "/assets/images/moments/moment-2.jpg",
    // Describes the photograph, not the keyword: a black and white close-up of
    // a bride holding a bouquet of small white flowers, her embroidered sleeve
    // in soft focus. Nothing about seating is visible in it, and saying
    // otherwise would be a lie told to exactly the readers who cannot see it.
    alt: "A black and white close-up of a bride holding a bouquet of small white flowers, her embroidered tulle sleeve in soft focus",
    width: 6983,
    height: 3436,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `A guest list tells you who is coming. A seating plan decides where they will be.`,
    `Creating a wedding seating plan can feel like a puzzle with too many pieces.`,
    `You have couples, families, friends who have known each other for years, guests meeting for the first time, plus-ones, different preferences — and sometimes relationships that need a little more thought than a name on a list suggests.`,
    `The easiest way to make it manageable is not to start with the tables.`,
    `Start with the people.`,
    `Once you know exactly who will be at your reception, you can build the plan around the relationships, practical constraints and atmosphere you want your wedding to have.`,
    `Here is a simple way to go from confirmed RSVPs to a seating plan you can actually use.`,
  ],

  sections: [
    {
      heading: "1. Decide Whether You Need Assigned Seating",
      content: [
        `Before creating a seating plan, decide how much structure your reception actually needs.`,
        `Some weddings work well with open seating, where guests choose any available place at their table. Others benefit from assigning guests to specific tables, particularly when you have a larger guest list or a carefully arranged reception.`,
        `There is also a middle ground: assigning guests to tables without assigning individual seats.`,
        `Your choice usually depends on:`,
        {
          type: "list",
          items: [
            `the size and layout of your reception`,
            `the number of guests`,
            `whether tables have fixed capacities`,
            `whether your venue has specific seating requirements`,
            `how formal or relaxed you want the reception to feel`,
          ],
        },
        `If guests are assigned to tables, you already have a seating plan in the practical sense. You only need individual seat assignments if your reception requires them.`,
        `The goal is not to make the plan as detailed as possible.`,
        `The goal is to give everyone a comfortable place to be.`,
      ],
    },
    {
      heading: "2. Start With Your RSVP Responses",
      content: [
        `Once you know that you need assigned seating, resist the temptation to open a blank seating chart and start placing names.`,
        `Start with your RSVP responses.`,
        `Your first question is simple:`,
        `Who is actually coming to the reception?`,
        `Your guest list may contain people who have:`,
        {
          type: "list",
          items: [
            `confirmed their attendance`,
            `declined`,
            `not responded yet`,
            `requested or added a companion`,
            `confirmed attendance for one part of the wedding but not another`,
          ],
        },
        `Only some of these people belong in your reception seating plan.`,
        `This is why creating the seating plan from an up-to-date RSVP picture is so much easier than maintaining a separate list by hand.`,
        {
          type: "p",
          text: [
            `If your RSVP process is still being organized, start by `,
            { text: "organizing wedding RSVPs", href: "/wedding-rsvp" },
            ` before you build the seating plan.`,
          ],
        },
      ],
    },
    {
      heading: "3. Separate Ceremony and Reception Attendance",
      content: [
        `A wedding can have several moments, and not every guest necessarily attends all of them.`,
        `Someone might attend the ceremony but not the reception. Another guest might be invited to both.`,
        `For seating purposes, what matters is the people who will actually be sitting down at the reception.`,
        `So before assigning anyone to a table, make sure your list reflects reception attendance, rather than simply your overall wedding headcount.`,
        `This small distinction can prevent a surprisingly common problem: building a seating plan around people who do not need a seat at the reception.`,
        `It also gives you a much more reliable starting point for calculating table capacity.`,
      ],
    },
    {
      heading: "4. Build Your Tables Before You Place People",
      content: [
        `Now turn your attention to the tables.`,
        `Start by establishing the practical structure of the room:`,
        {
          type: "list",
          items: [
            `How many tables are available?`,
            `How many guests can comfortably sit at each one?`,
            `Are the tables round, rectangular or another shape?`,
            `Are there tables with fixed positions?`,
            `Are there areas of the room that affect how guests should be grouped?`,
          ],
        },
        `At this stage, you do not need to decide who sits where.`,
        `You are simply creating the framework into which your guests will eventually fit.`,
        `This matters because seating is much easier when you know the boundaries first.`,
        `Instead of asking:`,
        `"Where can I put these eight people?"`,
        `you can ask:`,
        `"Which of my available tables is the right home for these eight people?"`,
      ],
    },
    {
      heading: "5. Group Guests by How They Know Each Other",
      content: [
        `Your guest list is made up of relationships, not just names.`,
        `Before assigning tables, look at the natural groups within it.`,
        {
          type: "list",
          items: [
            `immediate family`,
            `extended family`,
            `the wedding party`,
            `childhood friends`,
            `university friends`,
            `work colleagues`,
            `couples`,
            `relatives who already know each other`,
            `guests who are coming alone`,
          ],
        },
        `These groups are useful starting points because people tend to feel more comfortable when at least some of the people around them are familiar.`,
        `But don't treat them as rules.`,
        `A table made entirely from one existing group can sometimes be less enjoyable than a thoughtfully mixed table. A friend who knows several different groups may be a natural bridge between them.`,
        `Think of your groups as building blocks, not boxes that everyone must stay inside.`,
      ],
    },
    {
      heading: "6. Decide Who Should Sit Together — and Who Shouldn't",
      content: [
        `This is usually the most human part of the seating plan.`,
        `Start with the easy decisions.`,
        `Who would naturally enjoy spending a few hours together?`,
        `Then consider the more difficult ones.`,
        `Are there guests who:`,
        {
          type: "list",
          items: [
            `have specifically asked to sit together?`,
            `would benefit from knowing someone at their table?`,
            `have very different social circles?`,
            `have a difficult history with another guest?`,
            `may feel uncomfortable being seated together?`,
            `are likely to enjoy meeting new people?`,
          ],
        },
        `A guest's preference is useful information, but it does not always have a perfect one-to-one solution.`,
        `For example, one person may want to sit with someone who has already requested to sit elsewhere. Or a group may be too large to fit comfortably around one table.`,
        `Preferences are inputs to the seating decision, not instructions that must always be followed.`,
        `When you cannot satisfy every preference, prioritize overall comfort and the experience of the table as a whole.`,
      ],
    },
    {
      heading: "7. Choose the Right Number of Guests Per Table",
      content: [
        `There is no single number that works for every wedding table.`,
        `The right capacity depends on the table itself, your venue, the amount of space available and how comfortably you want guests to sit.`,
        `A table that technically fits ten people is not necessarily the best table for ten people if the room is tight or the layout makes conversation difficult.`,
        `Consider:`,
        {
          type: "list",
          items: [
            `table shape`,
            `table size`,
            `venue space`,
            `chairs and surrounding clearance`,
            `catering requirements`,
            `the atmosphere you want`,
          ],
        },
        `The important thing is to decide your practical limits before filling the tables.`,
      ],
    },
    {
      heading: "8. Place Guests at Each Table",
      content: [
        `Now you can start assigning people.`,
        {
          type: "subheading",
          text: "Start with the groups that have the fewest alternatives",
        },
        `Some guests are easy to move between tables. Others are connected to a particular group or have specific considerations.`,
        `Place the least flexible groups first.`,
        {
          type: "subheading",
          text: "Keep couples and parties together where appropriate",
        },
        `If guests are attending as a couple or party, don't accidentally separate them simply because two tables have different amounts of space.`,
        {
          type: "subheading",
          text: "Fill the remaining capacity thoughtfully",
        },
        `Once the less flexible groups are placed, use the remaining spaces to connect compatible guests and balance the tables.`,
        {
          type: "subheading",
          text: "Look at the table as a whole",
        },
        `Don't judge a table only by whether every seat is filled.`,
        `Ask:`,
        `"Would these people actually enjoy sitting together?"`,
        `A table with one empty seat can sometimes be better than a perfectly full table that feels socially awkward.`,
      ],
    },
    {
      heading: "9. Review the Plan for Problems",
      content: [
        `A seating plan isn't finished just because every guest has a table.`,
        `Before you consider it final, review it as a whole.`,
        `Check for:`,
        {
          type: "list",
          items: [
            `tables exceeding their capacity`,
            `guests who have not been assigned`,
            `couples or parties accidentally split`,
            `unresolved sit-with preferences`,
            `relationships that need another look`,
            `guests who may otherwise feel isolated`,
            `changes in RSVP status`,
            `companions whose attendance has changed`,
          ],
        },
        `This review is often where the quality of a seating plan improves most.`,
        `You may discover that moving one person solves several problems at once.`,
        `And remember: not every flagged issue means the seating plan is wrong.`,
        `Some are simply decisions that deserve another look.`,
      ],
    },
    {
      heading: "10. Recheck the Plan When Something Changes",
      content: [
        `Your seating plan is rarely finished the moment you first create it.`,
        `A guest may cancel. A companion may change. Someone who had not responded may confirm.`,
        `When that happens, don't assume that the entire plan needs to be rebuilt.`,
        `Start with the table affected by the change.`,
        `Then check:`,
        {
          // The numbering lives in the <ol>, so the source strings carry none.
          type: "steps",
          items: [
            `Does the table still have a sensible capacity?`,
            `Has a party been unintentionally split?`,
            `Does the change affect another guest's placement?`,
            `Are any preferences now harder to satisfy?`,
            `Does the overall plan still make sense?`,
          ],
        },
        `The important thing is to review the consequences of the change, rather than quietly assuming that everything else is still correct.`,
        `A good seating plan is not one that never changes.`,
        `It is one that is easy to review when things do.`,
      ],
    },
    {
      heading: "11. Prepare the Final Seating Information",
      content: [
        `Once you've reviewed the plan, turn it into something people can actually use on the wedding day.`,
        `Depending on your setup, this might include:`,
        {
          type: "list",
          items: [
            `final table assignments`,
            `table numbers or names`,
            `a guest lookup list`,
            `a version for your venue or coordinator`,
            `a printed copy for the reception`,
            `a digital copy for your own records`,
          ],
        },
        `Keep the final version simple.`,
        `On the day itself, nobody wants to interpret a complicated spreadsheet just to find their table.`,
        `The final plan should make one thing immediately clear:`,
        `who sits where.`,
      ],
    },
    {
      heading: "A Simpler Way From RSVP to Seating",
      content: [
        `Many seating tools start with a blank canvas.`,
        `Agapaé starts somewhere different: with the guests already in your wedding's RSVP and guest-management flow.`,
        `Once your reception attendees are confirmed and approved, Agapaé gives you the people who belong in the seating plan, along with their party context. You can create tables, place guests individually or as a party, work within table capacities, review seating issues and make changes as your plans evolve.`,
        `If guest information changes later, Agapaé does not silently rearrange the plan for you. The affected seating can be reviewed so that you remain in control of the final decision.`,
        `Once the plan is ready, seating assignments can be exported to Excel or a print-ready A4 landscape PDF.`,
        {
          type: "note",
          text: `Agapaé's seating tools are available on the Heirloom plan. The guide above explains the planning process; the product page shows how the seating experience works in Agapaé.`,
        },
        `The idea is simple:`,
        `RSVP → approved reception attendees → tables → seating → review → final plan.`,
        `No separate seating guest list to rebuild from scratch.`,
        {
          type: "p",
          text: [
            {
              text: "Explore wedding seating in Agapaé",
              href: "/wedding-seating",
            },
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set: Google
  // restricts FAQ rich results to government and health sites, so the markup
  // would buy nothing while committing us to a second copy of every answer.
  faq: [
    {
      q: `When should I create my wedding seating plan?`,
      a: `It is usually best to start once you have a reliable picture of your reception attendance. You can create an early draft before every RSVP is finalized, but expect to review it as responses change.`,
    },
    {
      q: `Should I wait until all guests have RSVP'd?`,
      a: `Not necessarily. You can begin grouping guests and planning your tables earlier, but your final seating plan should be based on the people actually attending the reception.`,
    },
    {
      q: `Should couples sit together at a wedding?`,
      a: `In most cases, couples will expect to sit together, unless there is a particular reason to make a different arrangement. Treat the circumstances and preferences of each couple or party individually rather than applying one rule to everyone.`,
    },
    {
      q: `How many guests should sit at each wedding table?`,
      a: `There is no universal number. It depends on the table's shape and size, your venue, available space and the atmosphere you want to create. Your venue's recommendations should also be taken into account.`,
    },
    {
      q: `What should I do if a guest cancels after I've made the seating plan?`,
      a: `Review the table affected by the cancellation and check whether the change affects other guests, party groupings or table capacity. You may only need to make a small adjustment rather than rebuild the entire plan.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Empty until /guides/how-many-people-wedding-table and
    // /guides/wedding-seating-chart-vs-seating-plan are registered. The test
    // refuses a slug that is not in the registry, so a link here cannot 404.
    guides: [],

    product: [
      {
        href: "/wedding-seating",
        label: "Wedding seating",
        blurb:
          "Plan wedding seating from the guests already in your RSVP flow, create tables, review issues and prepare a final seating plan in Agapaé.",
      },
    ],
  },
};

export default weddingSeatingPlanEn;
